<template>
  <div class="page-contact">
  <main>
      <section class="relative overflow-hidden bg-gray-50">
        <div class="absolute inset-0 bg-gradient-to-r from-brand/5 via-transparent to-brand/5"></div>
        <div class="mx-auto max-w-[1400px] px-4 py-16 lg:grid lg:grid-cols-2 lg:items-center lg:gap-12 lg:px-8 lg:py-20">
          <div class="relative z-10 space-y-4">
            <p class="text-xs font-semibold uppercase text-muted">Contact us</p>
            <h1 class="text-3xl font-semibold leading-tight text-ink sm:text-4xl">Tell us what you need. We’re here to help.</h1>
            <p class="text-lg text-muted">Questions about your account, an event, or a business listing? Send the right context and the Invi8 team can point you in the right direction.</p>
            <img src="/assets/illustrations/contact-us.svg" alt="A person contacting support" class="h-56 w-full rounded-3xl bg-orange-50/60 object-contain p-5" />
            <div class="grid gap-3 sm:grid-cols-2">
              <div class="rounded-2xl bg-white p-4 shadow-card ring-1 ring-gray-100">
                <p class="text-sm font-semibold text-ink">Concierge chat</p>
                <p class="text-sm text-muted">Help with invites, guests and accounts</p>
              </div>
              <div class="rounded-2xl bg-white p-4 shadow-card ring-1 ring-gray-100">
                <p class="text-sm font-semibold text-ink">Vendor support</p>
                <p class="text-sm text-muted">Listing, profile and enquiry support</p>
              </div>
            </div>
          </div>
          <div class="relative z-10 mt-10 lg:mt-0">
            <form class="space-y-4 rounded-3xl bg-white p-6 shadow-card ring-1 ring-gray-100" @submit.prevent="submitContact">
              <div class="grid gap-4 sm:grid-cols-2">
                <div>
                  <label class="text-sm font-semibold text-ink" for="contact-name">Name</label>
                  <input id="contact-name" v-model.trim="contactForm.name" required autocomplete="name" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm text-ink placeholder:text-muted focus:border-brand focus:outline-none focus:ring-4 focus:ring-orange-100" placeholder="Your name" />
                </div>
                <div>
                  <label class="text-sm font-semibold text-ink" for="contact-email">Email</label>
                  <input id="contact-email" v-model.trim="contactForm.email" required autocomplete="email" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm text-ink placeholder:text-muted focus:border-brand focus:outline-none focus:ring-4 focus:ring-orange-100" type="email" placeholder="you@example.com" />
                </div>
              </div>
              <div>
                <label class="text-sm font-semibold text-ink" for="contact-type">I’m reaching out as</label>
                <select id="contact-type" v-model="contactForm.type" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm text-ink focus:border-brand focus:outline-none focus:ring-4 focus:ring-orange-100">
                  <option>Host</option>
                  <option>Vendor</option>
                  <option>Partner</option>
                  <option>Press</option>
                </select>
              </div>
              <div>
                <label class="text-sm font-semibold text-ink" for="contact-message">Message</label>
                <textarea id="contact-message" v-model.trim="contactForm.message" required rows="4" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm text-ink placeholder:text-muted focus:border-brand focus:outline-none focus:ring-4 focus:ring-orange-100" placeholder="Include the event, account, or listing details that will help us understand the issue."></textarea>
              </div>
              <button class="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:cursor-not-allowed disabled:opacity-50" type="submit" :disabled="!canSubmit"><i class="ri-mail-send-line"></i> Open email message</button>
              <p class="text-xs leading-5 text-muted">This opens your email app with the details filled in. You can also write directly to <a class="font-semibold text-brand" href="mailto:hello@invi8.com">hello@invi8.com</a>.</p>
            </form>
          </div>
        </div>
      </section>

      <section class="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
        <div class="grid gap-6 md:grid-cols-3">
          <div class="rounded-3xl bg-gray-50 p-6 ring-1 ring-gray-100">
            <p class="text-sm font-semibold text-ink">Account support</p>
            <p class="mt-2 text-sm text-muted">Include the email connected to your account so we can identify it.</p>
          </div>
          <div class="rounded-3xl bg-gray-50 p-6 ring-1 ring-gray-100">
            <p class="text-sm font-semibold text-ink">Event support</p>
            <p class="mt-2 text-sm text-muted">Share the event name and date to help us understand the issue.</p>
          </div>
          <div class="rounded-3xl bg-gray-50 p-6 ring-1 ring-gray-100">
            <p class="text-sm font-semibold text-ink">Business support</p>
            <p class="mt-2 text-sm text-muted">Add your business name and listing link when asking about a vendor profile.</p>
          </div>
        </div>
      </section>

      <section class="bg-gray-900">
        <div class="mx-auto max-w-[1400px] px-4 py-12 text-white lg:px-8">
          <div class="grid gap-8 lg:grid-cols-2 lg:items-center">
            <div class="space-y-4">
              <p class="text-xs font-semibold uppercase text-gray-400">FAQs</p>
              <h2 class="text-3xl font-semibold leading-tight">Answers for hosts and event professionals.</h2>
              <p class="text-lg text-gray-300">Still need help? Send us a message with the details above.</p>
            </div>
            <div class="space-y-3">
              <details class="group rounded-2xl bg-white/5 p-4">
                <summary class="flex cursor-pointer items-center justify-between text-sm font-semibold">
                  How do I contact a vendor on Invi8?
                  <span class="text-brand">+</span>
                </summary>
                <p class="mt-2 text-sm text-gray-300">Open a vendor or venue profile and use its enquiry action. Include your event date, location and requirements.</p>
              </details>
              <details class="group rounded-2xl bg-white/5 p-4">
                <summary class="flex cursor-pointer items-center justify-between text-sm font-semibold">
                  Where can I manage my invitations?
                  <span class="text-brand">+</span>
                </summary>
                <p class="mt-2 text-sm text-gray-300">Sign in to your host account to create an invitation, manage guests, view feedback and update event settings.</p>
              </details>
              <details class="group rounded-2xl bg-white/5 p-4">
                <summary class="flex cursor-pointer items-center justify-between text-sm font-semibold">
                  How can a business join Invi8?
                  <span class="text-brand">+</span>
                </summary>
                <p class="mt-2 text-sm text-gray-300">Choose “Become a Vendor” to create a business account, complete your profile and add the services or venues you offer.</p>
              </details>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { computed, reactive } from 'vue'

const contactForm = reactive({
  name: '',
  email: '',
  type: 'Host',
  message: ''
})

const canSubmit = computed(() => Boolean(contactForm.name && contactForm.email && contactForm.message))

const submitContact = () => {
  if (!canSubmit.value) return
  const subject = encodeURIComponent(`Invi8 ${contactForm.type} support request`)
  const body = encodeURIComponent([
    `Name: ${contactForm.name}`,
    `Email: ${contactForm.email}`,
    `Contact type: ${contactForm.type}`,
    '',
    contactForm.message
  ].join('\n'))
  window.location.href = `mailto:hello@invi8.com?subject=${subject}&body=${body}`
}
</script>
